import type {
  ClaireBucketDelta,
  ClaireBucketKey,
  ClaireBucketWeek,
  ClaireHandoffWindow,
} from '@borradh-workspace/api-client/types';

/** "26 Oct" from a `2026-10-26` calendar day (no clock involved). */
export function formatDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1)).toLocaleDateString(
    'en-GB',
    { day: 'numeric', month: 'short', timeZone: 'UTC' }
  );
}

/** "64%" (whole percent, for the big numbers), or "–". */
export const formatPct = (share: number | null | undefined) =>
  share === null || share === undefined || Number.isNaN(share)
    ? '–'
    : `${Math.round(share * 100)}%`;

export type OutcomeKey = 'handled' | 'handedOff' | 'failing';

/** The Task Completion outcomes, for the explorer's badges and filter. */
export const OUTCOMES: readonly {
  key: OutcomeKey;
  filter: 'handled' | 'handed_off' | 'failing';
  label: string;
  /** Tailwind text classes. */
  text: string;
}[] = [
  {
    key: 'handled',
    filter: 'handled',
    label: 'Handled by Claire',
    text: 'text-green-600 dark:text-green-400',
  },
  {
    key: 'handedOff',
    filter: 'handed_off',
    label: 'Handed to staff',
    text: 'text-blue-700 dark:text-blue-300',
  },
  {
    key: 'failing',
    filter: 'failing',
    label: 'Failing',
    text: 'text-red-600 dark:text-red-400',
  },
];

export type BucketKey = ClaireBucketKey;

/**
 * The four outcomes the page leads with, in bar order, each with the
 * explorer filter behind its number and which way is good.
 */
export const BUCKETS: readonly {
  key: BucketKey;
  label: string;
  /** Tailwind background for the bar; a softer one for the weekly chart. */
  fill: string;
  fillSoft: string;
  good: boolean;
  filter: {
    outcome?: 'handled' | 'handed_off' | 'failing';
    mistake?: 'serious' | 'none';
  };
}[] = [
  {
    key: 'right',
    label: 'Right first time',
    fill: 'bg-green-600',
    fillSoft: 'bg-green-300',
    good: true,
    filter: { outcome: 'handled', mistake: 'none' },
  },
  {
    key: 'serious',
    label: 'Serious mistake',
    fill: 'bg-red-600',
    fillSoft: 'bg-red-300',
    good: false,
    filter: { mistake: 'serious' },
  },
  {
    key: 'handedOff',
    label: 'Handed to staff',
    fill: 'bg-blue-600',
    fillSoft: 'bg-blue-200',
    good: false,
    filter: { outcome: 'handed_off', mistake: 'none' },
  },
  {
    key: 'stuck',
    label: 'Stuck or silent',
    fill: 'bg-slate-700',
    fillSoft: 'bg-slate-700',
    good: false,
    filter: { outcome: 'failing', mistake: 'none' },
  },
];

/** "±3" in points, or "" when nothing was judged. */
export const marginText = (margin: number | null) =>
  margin === null ? '' : `±${Math.round(margin * 100)}`;

/**
 * "+0.6 pts, within noise" / "−2.5 pts, real change". `good` says which
 * direction is welcome, so a real change can be coloured; null when a move
 * has no direction worth colouring.
 */
export function changeText(
  change: ClaireBucketDelta,
  up: boolean
): { text: string; real: boolean; good: boolean | null } | null {
  if (change.delta === null) return null;
  const pts = change.delta * 100;
  const sign = pts > 0 ? '+' : pts < 0 ? '−' : '';
  const text = `${sign}${Math.abs(pts).toFixed(1)} pts on the 7 days before, ${change.real ? 'real change' : 'within noise'}`;
  const good = change.real && pts !== 0 ? pts > 0 === up : null;
  return { text, real: change.real, good };
}

/** "w/c 27 Oct". */
export const weekLabel = (w: Pick<ClaireBucketWeek, 'from'>) =>
  `w/c ${formatDay(w.from)}`;

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

/**
 * The live judge's handoff split in words. Percentages only from
 * `minJudged` scored handoffs: below that one verdict moves the split by
 * ten points or more, so the counts stand alone.
 */
export function handoffSplitText(
  window: ClaireHandoffWindow,
  minJudged: number
): {
  /** The lead-in before the right / avoidable counts. */
  lead: string;
  right: string;
  avoidable: string;
  /** Why there are no percentages; null when there are. */
  caveat: string | null;
  missing: string;
} {
  const pct = (share: number | null) =>
    window.judged >= minJudged && share !== null
      ? ` (${formatPct(share)})`
      : '';
  return {
    lead:
      window.judged === 0
        ? 'Judged by the live judge: no handoffs yet.'
        : `Judged by the live judge: ${plural(window.judged, 'handoff', 'handoffs')},`,
    right: `${window.right} right${pct(window.rightShare)}`,
    avoidable: `${window.unneeded} avoidable${pct(window.unneededShare)}`,
    caveat:
      window.judged > 0 && window.judged < minJudged
        ? `Too few for percentages: fewer than ${minJudged} scored.`
        : null,
    missing: `${plural(window.missing, 'turn', 'turns')} should have been handed off and ${window.missing === 1 ? "wasn't" : "weren't"}`,
  };
}
